// Orator — shared map foundation. ONE place for Google Maps bootstrap + the canvas pin layer, so
// every map (cut, Home activity, integrity, etc.) renders consistently and fast. Painting one DOM
// marker per door freezes the browser at scale; instead we pre-render each marker bitmap ONCE per
// color and blit it per point on a single canvas overlay — thousands of pins in one pass.
import { loadGoogleMaps } from "./googlemaps";

const MAP_ID = (import.meta.env.VITE_GOOGLE_MAPS_MAP_ID as string) || "DEMO_MAP_ID";

export type MapPoint = { lat: number; lon: number; [k: string]: any };

// ── Robust bounding box — outlier-fenced auto-zoom ───────────────────────────────────────────────
// A few voters geocode far outside the district (bad rooftop / stale source record). Fitting the map
// to the raw min/max lets ONE stray pin zoom the whole map out to the state. robustBounds fences the
// box to the inlier range per axis (Tukey IQR, k=3 -> only CLEAR outliers dropped) clamped to the real
// data extent. Stray pins still RENDER where they are; they just don't drive the auto-zoom.
export type LatLonBox = { minLat: number; maxLat: number; minLon: number; maxLon: number };
export function robustBounds(pts: Array<{ lat: number; lon: number }>, k = 3): LatLonBox | null {
  const num = (v: any) => typeof v === "number" && isFinite(v);
  const lats = pts.map((p) => p.lat).filter(num).sort((a, b) => a - b);
  const lons = pts.map((p) => p.lon).filter(num).sort((a, b) => a - b);
  if (!lats.length) return null;
  const q = (arr: number[], p: number) => {
    const i = (arr.length - 1) * p, lo = Math.floor(i), hi = Math.ceil(i);
    return arr[lo] + (arr[hi] - arr[lo]) * (i - lo);
  };
  const fence = (arr: number[]): [number, number] => {
    const q1 = q(arr, 0.25), q3 = q(arr, 0.75), iqr = q3 - q1;
    return [Math.max(arr[0], q1 - k * iqr), Math.min(arr[arr.length - 1], q3 + k * iqr)];
  };
  const [minLat, maxLat] = fence(lats);
  const [minLon, maxLon] = fence(lons);
  return { minLat, maxLat, minLon, maxLon };
}
/** Fit a Google map to an outlier-fenced box over the given points (no-op if none). */
export function fitRobust(google: any, map: any, pts: Array<{ lat: number; lon: number }>, padding = 40): void {
  const b = robustBounds(pts);
  if (!b) return;
  const bounds = new google.maps.LatLngBounds({ lat: b.minLat, lng: b.minLon }, { lat: b.maxLat, lng: b.maxLon });
  try { map.fitBounds(bounds, padding); } catch { /* projection not ready */ }
}

// ── Geo distance (meters) — for field-check logged-vs-rooftop offset ─────────────────────────────
export function haversineM(aLat: number, aLon: number, bLat: number, bLon: number): number {
  const R = 6371000, toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(bLat - aLat), dLon = toRad(bLon - aLon);
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(s)));
}

// ── Pre-rendered marker bitmaps (drawn once, cached, blitted per point) ──────────────────────────
const _bmp: Record<string, HTMLCanvasElement> = {};
function bitmap(key: string, w: number, h: number, paint: (ctx: CanvasRenderingContext2D) => void): HTMLCanvasElement {
  if (_bmp[key]) return _bmp[key];
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const c = document.createElement("canvas");
  c.width = Math.round(w * dpr); c.height = Math.round(h * dpr);
  const ctx = c.getContext("2d")!;
  ctx.scale(dpr, dpr);
  paint(ctx);
  _bmp[key] = c;
  return c;
}

export const PIN_W = 24, PIN_H = 32;
/** Classic teardrop pin (anchored at its bottom tip). Optional route number, or the do-not-contact ⊘.
 * No opts = the original plain pin (unchanged), so existing callers are pixel-identical. */
export function teardropIcon(
  color: string,
  opts: { label?: string; dnc?: boolean; muted?: boolean } = {},
): HTMLCanvasElement {
  // `muted` = this door is already held by an active walkbook. Same shape and same colour, drawn
  // faded, so a precinct reads at a glance as "these are spoken for, these are left" rather than
  // hiding the taken doors entirely. Keyed separately so the bitmap cache does not collide.
  const key = "pin:" + color + (opts.muted ? ":muted" : "")
    + (opts.dnc ? ":dnc" : (opts.label ? ":" + opts.label : ""));
  return bitmap(key, PIN_W, PIN_H, (ctx) => {
    const body = new Path2D("M12 0 C5.4 0 0 5.4 0 12 C0 21 12 32 12 32 C12 32 24 21 24 12 C24 5.4 18.6 0 12 0 Z");
    if (opts.muted) ctx.globalAlpha = 0.32;
    ctx.fillStyle = color; ctx.strokeStyle = opts.muted ? "#c9c6c0" : "#ffffff"; ctx.lineWidth = 1.5;
    ctx.fill(body); ctx.stroke(body);
    if (opts.muted) ctx.globalAlpha = 1;
    if (opts.dnc) {
      ctx.beginPath(); ctx.arc(12, 12, 6, 0, Math.PI * 2); ctx.fillStyle = "#ffffff"; ctx.fill();
      ctx.strokeStyle = "#dc2626"; ctx.lineWidth = 1.6;
      ctx.beginPath(); ctx.arc(12, 12, 4.1, 0, Math.PI * 2); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(9.1, 9.1); ctx.lineTo(14.9, 14.9); ctx.stroke();
    } else if (opts.label != null && opts.label !== "") {
      ctx.beginPath(); ctx.arc(12, 12, 6.2, 0, Math.PI * 2); ctx.fillStyle = "#ffffff"; ctx.fill();
      ctx.fillStyle = color; ctx.textAlign = "center"; ctx.textBaseline = "middle";
      ctx.font = (opts.label.length >= 3 ? "700 8px" : "700 10px") + " -apple-system,Segoe UI,Roboto,sans-serif";
      ctx.fillText(opts.label, 12, 12.5);
    } else {
      ctx.beginPath(); ctx.arc(12, 12, 4.3, 0, Math.PI * 2); ctx.fillStyle = "rgba(255,255,255,0.92)"; ctx.fill();
    }
  });
}
/** Small filled dot (anchored at its center) — for dense, glance-only point clouds (Home activity). */
// `alpha` draws the SAME dot, recessive: a spot that is on the map for context rather than as part
// of what you are doing. The sign cut screen uses it for candidates that fall outside every drawn
// shape -- they must stay visible (you are about to draw a second book around them) without
// competing with the ones already in a book. Kept as one parameter on the shared icon rather than a
// second icon function, because "same dot, quieter" is a property of the dot, not a different mark.
export function dotIcon(color: string, r = 5, alpha = 1): HTMLCanvasElement {
  const s = (r + 2) * 2;
  return bitmap(`dot:${color}:${r}:${alpha}`, s, s, (ctx) => {
    ctx.globalAlpha = alpha;
    ctx.beginPath(); ctx.arc(s / 2, s / 2, r, 0, Math.PI * 2);
    ctx.fillStyle = color; ctx.fill();
    ctx.lineWidth = 1.5; ctx.strokeStyle = "#ffffff"; ctx.stroke();
    ctx.globalAlpha = 1;
  });
}

/** A HOLLOW DOT: the colour on the ring, the surface showing through the middle.
 *
 *  For a fact that is TRUE OF A PIN WITHOUT BEING WHAT THE PIN MEANS -- on the sign cutter, "this
 *  spot is already in somebody's book". Being in a book is paperwork, not a condition, so it must
 *  not spend the fill colour; before this it did, as a second grey one shade from the grey that
 *  means a sign has been collected, and the two were indistinguishable (Caleb, 2026-09-27: "picked
 *  up and assigned signs currently look the exact same").
 *
 *  Drawn at the same radius as the filled dot so a ring and a dot sit on the same grid, with the
 *  white outer stroke every pin here carries so it survives a dark basemap.
 */
export function ringIcon(color: string, r = 5, alpha = 1): HTMLCanvasElement {
  const s = (r + 2) * 2;
  return bitmap(`ring:${color}:${r}:${alpha}`, s, s, (ctx) => {
    ctx.globalAlpha = alpha;
    ctx.beginPath(); ctx.arc(s / 2, s / 2, r - 0.5, 0, Math.PI * 2);
    ctx.lineWidth = 2.5; ctx.strokeStyle = color; ctx.stroke();
    ctx.beginPath(); ctx.arc(s / 2, s / 2, r + 1, 0, Math.PI * 2);
    ctx.lineWidth = 1; ctx.strokeStyle = "#ffffff"; ctx.stroke();
    ctx.globalAlpha = 1;
  });
}

// Data-URI SVG teardrop for DOM / AdvancedMarkerElement markers (the walk map's click-to-open doors).
// Optional route number, or the do-not-contact ⊘. Kept pixel-matched to teardropIcon so a canvas map
// and a DOM map render the SAME pin. This is the one place the numbered-teardrop SVG lives now.
export function pinDataURI(fill: string, opts: { label?: string; dnc?: boolean } = {}): string {
  const head = "<svg xmlns='http://www.w3.org/2000/svg' width='26' height='34' viewBox='0 0 24 32'>" +
    "<path d='M12 0 C5.4 0 0 5.4 0 12 C0 21 12 32 12 32 C12 32 24 21 24 12 C24 5.4 18.6 0 12 0 Z' fill='" + fill + "' stroke='#ffffff' stroke-width='1.5'/>";
  let glyph: string;
  if (opts.dnc) {
    glyph = "<circle cx='12' cy='12' r='8' fill='#ffffff'/>" +
      "<circle cx='12' cy='12' r='5.4' fill='none' stroke='#dc2626' stroke-width='1.9'/>" +
      "<line x1='8.2' y1='8.2' x2='15.8' y2='15.8' stroke='#dc2626' stroke-width='1.9'/>";
  } else {
    const label = opts.label ?? "";
    const fs = label.length >= 3 ? 8.5 : 11;
    glyph = "<circle cx='12' cy='12' r='8' fill='#ffffff'/>" +
      "<text x='12' y='" + (fs >= 11 ? "15.6" : "15.2") + "' text-anchor='middle' font-family='-apple-system,Segoe UI,Roboto,sans-serif' font-size='" + fs + "' font-weight='700' fill='" + fill + "'>" + label + "</text>";
  }
  return "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(head + glyph + "</svg>");
}

// ── Map bootstrap — the single loader + height-gate + standard options ───────────────────────────
// (Google paints only a ~70px strip if created against a 0-height container, so we gate on real
// height first.) Returns once the map exists.
export function createOratorMap(
  el: HTMLDivElement,
  opts: { center?: { lat: number; lng: number }; zoom?: number; minHeight?: number; options?: any } = {},
): Promise<{ google: any; map: any }> {
  const minHeight = opts.minHeight ?? 400;
  return loadGoogleMaps().then((google: any) => new Promise((resolve, reject) => {
    if (!el) { reject(new Error("map container gone")); return; }

    // ── WAITING FOR HEIGHT IS A SIZE QUESTION, NOT AN ANIMATION QUESTION (2026-09-25) ─────────
    //
    // This gate used to be a requestAnimationFrame loop: check the height, and if it is short,
    // ask again next frame, up to 300 times, then build the map anyway. Two things are wrong with
    // that, and the second one is why the dashboard's maps come up blank.
    //
    // FIRST: rAF DOES NOT TICK IN A BACKGROUND TAB. Open the dashboard in a tab you are not
    // looking at and the loop simply stops -- the map is never constructed at all. Measured on
    // staging 2026-09-25: `document.querySelectorAll('.gm-style').length === 0` on the cut screen
    // after twenty seconds, with the Maps library fully loaded and no error anywhere.
    //
    // SECOND, AND WORSE: "then build the map anyway". A map constructed against a container that
    // is still the wrong size gets a degenerate viewport, and a Google map with a degenerate
    // viewport never asks for a single basemap tile. It does not throw. It does not warn. The
    // console is clean. Our own overlays still draw, because we draw those ourselves against the
    // projection -- so you get the district outline and the dots floating on an empty page, which
    // is exactly what the cut screen and the Signs map have been showing.
    //
    // That also explains the thing that looked like a styling bug: clicking Satellite "fixed" the
    // map. It was never about imagery. Changing the map type forces the map to re-evaluate its
    // viewport, which finally makes it fetch. Any nudge would have done it.
    //
    // So: wait on a ResizeObserver, which fires on the thing we actually care about and does not
    // depend on the tab being visible. And keep a deadline, because a container that is genuinely
    // shorter than minHeight on a small window must still get a map rather than nothing.
    const ready = () => el.offsetHeight >= minHeight && el.offsetWidth > 0;
    let built = false;
    let waitRO: ResizeObserver | null = null;
    let deadline: ReturnType<typeof setTimeout> | null = null;

    const build = () => {
      if (built) return;
      built = true;
      if (waitRO) { waitRO.disconnect(); waitRO = null; }
      if (deadline) { clearTimeout(deadline); deadline = null; }

      // THE STANDARD (Caleb, 2026-09-23): every map opens on the plain map, never satellite. The
      // imagery is a deliberate choice on the two screens that are about looking at a place (Signs,
      // the tag overlay), and one fewer control everywhere else -- "one more toggle on a screen that
      // adds clutter that people aren't gonna really use when they're walkbook cutting".
      const map = new google.maps.Map(el, {
        center: opts.center ?? { lat: 31, lng: -99 }, zoom: opts.zoom ?? 6, mapId: MAP_ID,
        mapTypeId: "roadmap",
        mapTypeControl: false, streetViewControl: false, fullscreenControl: false, gestureHandling: "greedy",
        ...(opts.options || {}),
      });

      // AND A MAP THAT WAS BUILT AT A BAD MOMENT MUST BE ABLE TO RECOVER.
      //
      // The gate above makes the bad moment unlikely; it cannot make it impossible. A container
      // that gains its height later -- a panel unhidden, a window restored, a layout that settles
      // one frame after we looked -- leaves a map that has already decided on a viewport and will
      // sit there, tileless and silent, for as long as the page is open. Re-centring on the centre
      // it already has is the cheapest nudge that makes it re-evaluate, and it moves nothing.
      //
      // Once. A map that has fetched does not need nudging again, and an observer left running for
      // the life of the page would fire on every drag of a split pane.
      let nudged = false;
      const healRO = new ResizeObserver(() => {
        if (nudged || !el.offsetHeight) return;
        const c = map.getCenter();
        if (!c) return;
        nudged = true;
        map.setCenter(c);
        healRO.disconnect();
      });
      healRO.observe(el);

      resolve({ google, map });
    };

    if (ready()) { build(); return; }
    waitRO = new ResizeObserver(() => { if (ready()) build(); });
    waitRO.observe(el);
    // Five seconds is the old loop's 300 frames, expressed in the unit a person can reason about.
    // Building late beats never building: the heal observer above covers the case where the size
    // arrives after this.
    deadline = setTimeout(build, 5000);
  }));
}

// ── THE LINES EVERY DASHBOARD MAP SHOULD CARRY (2026-09-23) ──────────────────────────────────
//
// The two styles live HERE, in the file both apps share, and precinctstyle.ts re-exports them: the
// cutter had owned the district style since long before any other map drew one, and a second copy
// of "the campaign's outline is this red" is exactly the drift this guard exists to prevent.
/** The campaign's own outline: bold line, no fill, non-clickable so clicks pass through. */
export const DISTRICT_STYLE = { fillOpacity: 0, strokeColor: "#b01919", strokeWeight: 3.5,
                                clickable: false, zIndex: 1 };
/** Precinct edges with the coverage lens off, and with it on but this precinct not open. */
export const PRECINCT_STROKE = "#64748b";
//
// Caleb: "we don't have district lines visible -- you should be able to see your district lines any
// time you're viewing this stuff in the dashboard", and precinct lines with them. The cut map has
// drawn both for months; every other map was a field of dots with no idea where the district was.
// One helper so a new map gets them by asking, and they are the same lines everywhere.
export type BoundaryLayers = { setPrecincts: (on: boolean) => void;
                               /** Repaint the picked set. No-op on a map that passed no onPick. */
                               setSelected: (keys: string[]) => void;
                               /** Turn picking on and off without rebuilding the layer. */
                               setPickable: (on: boolean) => void;
                               destroy: () => void };
/** A PICKED PRECINCT, in the cutter's green -- the same green the walkbook cutter's map uses for
 *  the same state, because it means the same thing: this ground is going in a book. */
const PRECINCT_PICKED = "#1a8a4a";

export function addBoundaryLayers(
  google: any, map: any,
  opts: { district?: any; precincts?: any; showPrecincts?: boolean;
          /** ── WHICH POLYGONS ANSWER, AND WHAT THEY ARE CALLED (2026-09-28) ─────────────────
           *  The caller owns this, because only the caller knows which precincts are on offer.
           *  It returns the id of a polygon somebody may pick, or null for one that is only
           *  context -- and a null polygon is drawn faint AND made click-through, so a click on
           *  it reaches the map underneath instead of being swallowed by a shape that was never
           *  going to do anything. Without this every polygon is context. */
          precinctIdOf?: (props: { fips?: unknown; name?: unknown }) => string | null;
          /** Given, clicking a pickable polygon reports its id. */
          onPickPrecinct?: (id: string) => void;
          /** Ids currently picked, for the fill. Kept in step with setSelected. */
          selectedPrecincts?: string[] },
): BoundaryLayers {
  let dl: any = null, pl: any = null;
  let picked = new Set(opts.selectedPrecincts ?? []);
  // A POLYGON THAT CAN BE CLICKED EATS THE CLICK. On the cut screens the same map is also how a
  // shape gets drawn, so picking is switched OFF unless the screen is actually asking for
  // precincts -- otherwise the first point of every custom shape would land on a precinct.
  // Starts OFF and is switched on by the screen, so a map is never briefly clickable before the
  // effect that owns the answer has run.
  let pickable = false;
  if (opts.district?.features?.length) {
    dl = new google.maps.Data({ map });
    dl.addGeoJson(opts.district);
    dl.setStyle(DISTRICT_STYLE);
  }
  // THE STYLE IS A FUNCTION, not an object, once a precinct can be picked: Google re-runs it per
  // feature on setStyle, which is how one repaint restyles only the polygons that changed hands.
  const idOf = (f: any) => (opts.precinctIdOf ? opts.precinctIdOf(propsOf(f)) : null);
  const style = (f: any) => {
    // THREE STATES, AND THE MIDDLE ONE IS THE ONE THAT WAS MISSING. Caleb, on the first cut of
    // this: "I can't click any of the precincts -- it doesn't highlight and select them." He was
    // clicking real precincts; they simply had no spots in them, so nothing was going to happen,
    // and the map gave him no way to know that. A precinct you CAN pick now says so before you
    // click it.
    const id = pickable ? idOf(f) : null;
    const on = !!id && picked.has(id);
    const can = !!id && !on;
    return { fillColor: PRECINCT_PICKED, fillOpacity: on ? 0.22 : 0,
             strokeColor: on || can ? PRECINCT_PICKED : PRECINCT_STROKE,
             strokeOpacity: on ? 1 : can ? 0.75 : 1,
             strokeWeight: on ? 2.5 : can ? 2 : 1,
             // Thin, grey and NOT clickable on a map whose job is the dots: there, a precinct edge
             // is context. Clickable belongs to a cutter, and only to the ones that answer.
             clickable: !!id, zIndex: on ? 3 : can ? 2 : 1 };
  };
  if (opts.precincts?.features?.length) {
    pl = new google.maps.Data({ map: opts.showPrecincts === false ? null : map });
    pl.addGeoJson(opts.precincts);
    pl.setStyle(style);
    if (opts.onPickPrecinct) {
      pl.addListener("click", (e: any) => {
        const id = pickable ? idOf(e.feature) : null;
        if (id) opts.onPickPrecinct!(id);
      });
    }
  }
  return {
    setPrecincts: (on: boolean) => { if (pl) pl.setMap(on ? map : null); },
    setSelected: (keys: string[]) => { picked = new Set(keys); if (pl) pl.setStyle(style); },
    setPickable: (on: boolean) => { pickable = on && !!opts.onPickPrecinct; if (pl) pl.setStyle(style); },
    destroy: () => { if (dl) dl.setMap(null); if (pl) pl.setMap(null); dl = null; pl = null; },
  };
}

/** google.maps.Data hides a feature's properties behind getProperty(), one key at a time, so the
 *  two the boundary layers care about are read out by name. */
function propsOf(f: any): { fips?: any; name?: any } {
  return { fips: f?.getProperty?.("fips"), name: f?.getProperty?.("name") };
}

/** Fit the map to the campaign's own outline.
 *
 *  FOR A MAP WITH NOTHING ON IT YET. `fitRobust` fits to the points, so a sign programme on its
 *  first day -- no signs, no planned corners -- had nothing to fit to and opened on the default
 *  view: the whole of Texas, and in the cut screen most of Mexico with it. The district is the one
 *  thing a campaign always has, and "where my campaign is" is the only sensible opening view for a
 *  map you are about to drop the first pin on.
 *
 *  Returns false when there is no outline to use, so the caller can fall back. */
export function fitDistrict(google: any, map: any, district: any): boolean {
  if (!district?.features?.length) return false;
  const b = new google.maps.LatLngBounds();
  let n = 0;
  // Walks any depth of GeoJSON coordinate nesting -- Polygon, MultiPolygon, and a FeatureCollection
  // holding both, which is what a campaign licensed across two districts actually returns.
  const walk = (c: any) => {
    if (typeof c?.[0] === "number" && typeof c?.[1] === "number") { b.extend({ lat: c[1], lng: c[0] }); n++; return; }
    if (Array.isArray(c)) for (const x of c) walk(x);
  };
  for (const f of district.features) walk(f?.geometry?.coordinates);
  if (!n) return false;
  try { map.fitBounds(b, 40); } catch { return false; }   // projection not ready
  return true;
}

export type PinLayer = {
  redraw: () => void;
  setMap: (m: any) => void;
  /** Nearest point to a clicked LatLng within radiusPx (screen px), or null — for click→info. */
  nearest: (latLng: any, radiusPx?: number) => MapPoint | null;
};

// ── Canvas pin layer — draws ALL points in one paint; redraws on map move + on demand ────────────
export function createPinLayer(
  google: any, map: any,
  opts: {
    getPoints: () => MapPoint[];
    iconFor: (p: MapPoint) => HTMLCanvasElement;
    anchor?: "tip" | "center";   // teardrop tip vs dot center
    iconW?: number; iconH?: number;
  },
): PinLayer {
  const iw = opts.iconW ?? PIN_W, ih = opts.iconH ?? PIN_H;
  const tip = (opts.anchor ?? "tip") === "tip";
  class Layer extends google.maps.OverlayView {
    canvas: HTMLCanvasElement | null = null;
    onAdd() {
      const cv = document.createElement("canvas");
      cv.style.position = "absolute"; cv.style.pointerEvents = "none"; cv.style.willChange = "transform";
      this.canvas = cv;
      // markerLayer is above overlayLayer (where the coverage-fill polygons draw), so door pins sit
      // ON TOP of the purple fill instead of under it. pointer-events stays none — clicks fall through.
      (this.getPanes() as any).markerLayer.appendChild(cv);
    }
    onRemove() { if (this.canvas && this.canvas.parentNode) this.canvas.parentNode.removeChild(this.canvas); this.canvas = null; }
    draw() {
      const cv = this.canvas; const proj = this.getProjection(); const m = this.getMap();
      if (!cv || !proj || !m) return;
      const b = m.getBounds && m.getBounds(); if (!b) return;
      const ne = proj.fromLatLngToDivPixel(b.getNorthEast());
      const sw = proj.fromLatLngToDivPixel(b.getSouthWest());
      const left = Math.min(sw.x, ne.x), top = Math.min(ne.y, sw.y);
      const w = Math.abs(ne.x - sw.x), h = Math.abs(sw.y - ne.y);
      if (w <= 0 || h <= 0) return;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      cv.style.left = left + "px"; cv.style.top = top + "px"; cv.style.width = w + "px"; cv.style.height = h + "px";
      cv.width = Math.round(w * dpr); cv.height = Math.round(h * dpr);
      const ctx = cv.getContext("2d"); if (!ctx) return;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0); ctx.clearRect(0, 0, w, h);
      const pts = opts.getPoints() || [];
      for (let k = 0; k < pts.length; k++) {
        const p = pts[k];
        const px = proj.fromLatLngToDivPixel(new google.maps.LatLng(p.lat, p.lon));
        const x = px.x - left, y = px.y - top;
        if (x < -iw || y < -ih || x > w + iw || y > h + ih) continue;
        ctx.drawImage(opts.iconFor(p), x - iw / 2, tip ? y - ih : y - ih / 2, iw, ih);
      }
    }
  }
  const layer: any = new Layer();
  layer.setMap(map);
  return {
    redraw: () => { try { layer.draw(); } catch { /* projection not ready */ } },
    setMap: (m: any) => layer.setMap(m),
    nearest: (latLng: any, radiusPx = 18) => {
      const proj = layer.getProjection(); if (!proj) return null;
      const click = proj.fromLatLngToContainerPixel(latLng); if (!click) return null;
      const pts = opts.getPoints() || [];
      let best: MapPoint | null = null, bestD = radiusPx * radiusPx;
      for (let k = 0; k < pts.length; k++) {
        const p = pts[k];
        const px = proj.fromLatLngToContainerPixel(new google.maps.LatLng(p.lat, p.lon)); if (!px) continue;
        const ay = tip ? px.y - ih / 2 : px.y;   // pin body center ≈ tip − h/2
        const dx = px.x - click.x, dy = ay - click.y;
        const d = dx * dx + dy * dy;
        if (d < bestD) { bestD = d; best = p; }
      }
      return best;
    },
  };
}

/* ── A HUNDRED THOUSAND DOORS ON ONE CANVAS ──────────────────────────────────────────────────────
 *
 *  createPinLayer above is right for a few thousand teardrops and wrong for a hundred thousand
 *  dots, and the reason is not the canvas -- it is what it does PER POINT, PER FRAME:
 *
 *      new google.maps.LatLng(p.lat, p.lon)      one allocation
 *      proj.fromLatLngToDivPixel(latLng)         one Maps API call
 *      ctx.drawImage(opts.iconFor(p), ...)       one image blit
 *
 *  At 4,000 points that is fine. At 120,000 it is 240,000 allocations and 120,000 API calls on
 *  every pan and every zoom frame, and the map stops being a map.
 *
 *  THE FIX IS THAT MERCATOR IS LINEAR IN WORLD COORDINATES. Google's projection maps lat/lon into a
 *  fixed 256x256 world square that does not depend on zoom or centre; screen pixels are then just
 *  (world - origin) * 2^zoom. So the expensive half is computed ONCE when the data arrives, into
 *  two Float64Arrays, and each frame derives the linear transform from exactly TWO projected
 *  corners -- two API calls per frame instead of a hundred and twenty thousand.
 *
 *  Deriving the transform from the corners rather than hard-coding 2^zoom is deliberate: it keeps
 *  working if Google changes its tile size or pixel origin, which hard-coded arithmetic would not.
 *
 *  DRAWING IS BATCHED BY COLOUR. Setting fillStyle is a state change; doing it per point costs more
 *  than the fill. Points are bucketed by class once, then painted in one pass per colour -- six
 *  state changes per frame for six classes, whatever the point count.
 *
 *  HIT TESTING IS A GRID, NOT A LOOP. createPinLayer's `nearest` walks every point allocating a
 *  LatLng each, which at this size is a frozen tab on every click. Points go into a uniform grid in
 *  world space when the data arrives; a click reads the nine cells around it.
 */

export type DenseData = { id: number[]; lat: number[]; lon: number[]; st: string };

export type DenseLayer = {
  setData: (d: DenseData | null) => void;
  redraw: () => void;
  setMap: (m: any) => void;
  /** Index into the arrays of the nearest point within radiusPx, or null. */
  hit: (latLng: any, radiusPx?: number) => number | null;
  /** Move one point and repaint, without refetching. Used while a pin is being dragged and again
   *  when the drag is undone -- the server is the record, but the dot has to follow the cursor. */
  moveTo: (index: number, lat: number, lon: number) => void;
  /** Which point is currently held, drawn with a halo so it is obvious it is picked up. */
  setHeld: (index: number | null) => void;
  destroy: () => void;
};

const WORLD = 256;
const worldX = (lon: number) => ((lon + 180) / 360) * WORLD;
const worldY = (lat: number) => {
  // Clamped to the Mercator limit: tan blows up at the poles and one bad row must not produce NaN
  // and blank the whole layer.
  const s = Math.sin(Math.max(-85.05, Math.min(85.05, lat)) * Math.PI / 180);
  return WORLD * (0.5 - Math.log((1 + s) / (1 - s)) / (4 * Math.PI));
};

export function createDenseLayer(
  google: any, map: any,
  opts: { colorFor: (cls: string) => string; radius?: number; classes?: string },
): DenseLayer {
  const R = opts.radius ?? 2;
  // The classes to paint, in back-to-front order: whatever the auditor most needs to see goes last
  // so it lands on top of the mass of ordinary doors.
  const CLASSES = opts.classes ?? "rlpbhs";

  let data: DenseData | null = null;
  let held: number | null = null;
  let wx: Float64Array = new Float64Array(0);
  let wy: Float64Array = new Float64Array(0);
  let buckets: number[][] = [];
  // Uniform grid over world space for hit testing. Cell size is in world units; 0.002 world units
  // is roughly 30 m at this latitude, so a cell holds a handful of doors in a dense subdivision.
  const CELL = 0.002;
  let grid: Map<number, number[]> = new Map();
  const key = (cx: number, cy: number) => cx * 1e6 + cy;

  function ingest(d: DenseData | null) {
    data = d;
    grid = new Map();
    buckets = CLASSES.split("").map(() => []);
    if (!d || !d.lat.length) { wx = new Float64Array(0); wy = new Float64Array(0); return; }
    const n = d.lat.length;
    wx = new Float64Array(n); wy = new Float64Array(n);
    for (let i = 0; i < n; i++) {
      const x = worldX(d.lon[i]), y = worldY(d.lat[i]);
      wx[i] = x; wy[i] = y;
      const b = CLASSES.indexOf(d.st[i] || "l");
      buckets[b < 0 ? 0 : b].push(i);
      const k = key(Math.floor(x / CELL), Math.floor(y / CELL));
      const cell = grid.get(k);
      if (cell) cell.push(i); else grid.set(k, [i]);
    }
  }

  class Dense extends google.maps.OverlayView {
    canvas: HTMLCanvasElement | null = null;
    onAdd() {
      const cv = document.createElement("canvas");
      cv.style.position = "absolute"; cv.style.pointerEvents = "none"; cv.style.willChange = "transform";
      this.canvas = cv;
      (this.getPanes() as any).markerLayer.appendChild(cv);
    }
    onRemove() {
      if (this.canvas && this.canvas.parentNode) this.canvas.parentNode.removeChild(this.canvas);
      this.canvas = null;
    }
    /** The two projected corners, and the linear world→pixel transform they imply. */
    frame() {
      const proj = this.getProjection(); const m = this.getMap();
      if (!proj || !m) return null;
      const b = m.getBounds && m.getBounds(); if (!b) return null;
      const nePix = proj.fromLatLngToDivPixel(b.getNorthEast());
      const swPix = proj.fromLatLngToDivPixel(b.getSouthWest());
      const ne = b.getNorthEast(), sw = b.getSouthWest();
      const neW = { x: worldX(ne.lng()), y: worldY(ne.lat()) };
      const swW = { x: worldX(sw.lng()), y: worldY(sw.lat()) };
      const dwx = neW.x - swW.x, dwy = swW.y - neW.y;
      // A viewport spanning the antimeridian, or a degenerate one mid-resize, yields a zero or
      // negative span. Bail rather than paint every point onto one pixel.
      if (!(dwx > 0) || !(dwy > 0)) return null;
      const sx = (nePix.x - swPix.x) / dwx, sy = (swPix.y - nePix.y) / dwy;
      if (!isFinite(sx) || !isFinite(sy) || sx <= 0 || sy <= 0) return null;
      const left = Math.min(swPix.x, nePix.x), top = Math.min(nePix.y, swPix.y);
      return { sx, sy, ox: swW.x, oy: neW.y, left, top,
               w: Math.abs(nePix.x - swPix.x), h: Math.abs(swPix.y - nePix.y) };
    }
    draw() {
      const cv = this.canvas; if (!cv) return;
      const f = this.frame(); if (!f || f.w <= 0 || f.h <= 0) return;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      cv.style.left = f.left + "px"; cv.style.top = f.top + "px";
      cv.style.width = f.w + "px"; cv.style.height = f.h + "px";
      cv.width = Math.round(f.w * dpr); cv.height = Math.round(f.h * dpr);
      const ctx = cv.getContext("2d"); if (!ctx) return;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, f.w, f.h);
      if (!data) return;
      const d = R * 2;
      for (let b = 0; b < buckets.length; b++) {
        const idx = buckets[b]; if (!idx.length) continue;
        ctx.fillStyle = opts.colorFor(CLASSES[b]);
        for (let k = 0; k < idx.length; k++) {
          const i = idx[k];
          if (i === held) continue;                 // drawn last, on top, with its halo
          const x = (wx[i] - f.ox) * f.sx, y = (wy[i] - f.oy) * f.sy;
          if (x < -d || y < -d || x > f.w + d || y > f.h + d) continue;
          ctx.fillRect(x - R, y - R, d, d);
        }
      }
      // A DARK HAIRLINE UNDER EVERY DOT. A bright dot is legible over tree canopy and vanishes over
      // a pale roof or a concrete lot; the outline is what makes one palette work on both. Drawn as
      // a second pass rather than per-dot stroking, which would be a state change per point.
      if (R >= 2) {
        ctx.strokeStyle = "rgba(0,0,0,.55)"; ctx.lineWidth = 1;
        for (let b = 0; b < buckets.length; b++)
          for (let k = 0; k < buckets[b].length; k++) {
            const i = buckets[b][k];
            if (i === held) continue;
            const x = (wx[i] - f.ox) * f.sx, y = (wy[i] - f.oy) * f.sy;
            if (x < -d || y < -d || x > f.w + d || y > f.h + d) continue;
            ctx.strokeRect(x - R - 0.5, y - R - 0.5, d + 1, d + 1);
          }
      }
      // THE HELD PIN, LAST AND UNMISTAKABLE. "You want some kind of mechanism where maybe it's
      // clear that I'm picking it up to move it." A ring plus a bigger dot, drawn over everything,
      // so at a glance you know which door you are holding and that you are holding one at all.
      if (held != null && data && held < data.lat.length) {
        const x = (wx[held] - f.ox) * f.sx, y = (wy[held] - f.oy) * f.sy;
        ctx.beginPath(); ctx.arc(x, y, R + 7, 0, Math.PI * 2);
        ctx.strokeStyle = "#ffffff"; ctx.lineWidth = 2.5; ctx.stroke();
        ctx.beginPath(); ctx.arc(x, y, R + 7, 0, Math.PI * 2);
        ctx.strokeStyle = "rgba(0,0,0,.65)"; ctx.lineWidth = 1; ctx.stroke();
        ctx.fillStyle = opts.colorFor(data.st[held] || "l");
        ctx.beginPath(); ctx.arc(x, y, R + 2.5, 0, Math.PI * 2); ctx.fill();
        ctx.strokeStyle = "#ffffff"; ctx.lineWidth = 1.5; ctx.stroke();
      }
    }
  }

  const layer: any = new Dense();
  layer.setMap(map);

  return {
    setData: (d) => { ingest(d); try { layer.draw(); } catch { /* projection not ready */ } },
    redraw: () => { try { layer.draw(); } catch { /* projection not ready */ } },
    setMap: (m: any) => layer.setMap(m),
    hit: (latLng: any, radiusPx = 8) => {
      if (!data || !wx.length) return null;
      const f = layer.frame(); if (!f) return null;
      const cx = worldX(latLng.lng()), cy = worldY(latLng.lat());
      // Search radius in world units, derived from the same transform the paint used.
      const rw = Math.max(radiusPx / f.sx, radiusPx / f.sy);
      const span = Math.max(1, Math.ceil(rw / CELL));
      const gx = Math.floor(cx / CELL), gy = Math.floor(cy / CELL);
      let best = -1, bestD = Infinity;
      for (let ix = gx - span; ix <= gx + span; ix++) {
        for (let iy = gy - span; iy <= gy + span; iy++) {
          const cell = grid.get(key(ix, iy)); if (!cell) continue;
          for (let k = 0; k < cell.length; k++) {
            const i = cell[k];
            const dx = (wx[i] - cx) * f.sx, dy = (wy[i] - cy) * f.sy;
            const dd = dx * dx + dy * dy;
            if (dd < bestD) { bestD = dd; best = i; }
          }
        }
      }
      return best >= 0 && bestD <= radiusPx * radiusPx ? best : null;
    },
    moveTo: (i, lat, lon) => {
      if (!data || i < 0 || i >= data.lat.length) return;
      // The grid is not reindexed. A pin moves metres and a cell is ~30 m, so at worst a hit test
      // for the moved pin checks a neighbouring cell it is no longer in -- and the search already
      // spans the surrounding cells. Rebuilding a 120,000-entry grid on every mousemove is not.
      data.lat[i] = lat; data.lon[i] = lon;
      wx[i] = worldX(lon); wy[i] = worldY(lat);
      try { layer.draw(); } catch { /* projection not ready */ }
    },
    setHeld: (i) => { held = i; try { layer.draw(); } catch { /* projection not ready */ } },
    destroy: () => { try { layer.setMap(null); } catch { /* already gone */ } },
  };
}
